import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "./components/ui/toaster";
import LoginPage from "./pages/LoginPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import SSOCallbackPage from "./pages/SSOCallbackPage";
import SelectInstancesPage from "./pages/SelectInstancesPage";
import WorkflowPage from "./pages/WorkflowPage";
import WorkflowDetailPage from "./pages/WorkflowDetailPage";
import ActionEffectMigrationPage from "./pages/ActionEffectMigrationPage";
import LogsPage from "./pages/LogsPage";
import SessionActivityPage from "./pages/SessionActivityPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import ConnectedServicesPage from "./pages/ConnectedServicesPage";
import { useAuthStore } from "./store/useAuthStore";
import ProtectedRoute from "./components/ProtectedRoute";
import SessionExpiredDialog from "./components/SessionExpiredDialog";
import { useIdleTimeout } from "./hooks/useIdleTimeout";

function App() {
  const { isAuthenticated } = useAuthStore();


  useIdleTimeout();

  return (
    <Router>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <div className="min-h-screen bg-background">
          <Routes>
            <Route
              path="/"
              element={
                isAuthenticated ?
                  <Navigate to="/select-instances" replace /> :
                  <LoginPage />
              }
            />
            {/* First step after login: pick the source→target pair before any workflow type. */}
            <Route
              path="/select-instances"
              element={
                <ProtectedRoute>
                  <SelectInstancesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sso/auth"
              element={<SSOCallbackPage />}
            />
            {/* Public: invite / password-reset links land here while logged out. */}
            <Route
              path="/set-password"
              element={<SetPasswordPage />}
            />
            <Route
              path="/workflows"
              element={
                <ProtectedRoute>
                  <WorkflowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflow"
              element={
                <ProtectedRoute>
                  <WorkflowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={<Navigate to="/workflows" replace />}
            />
            <Route
              path="/workflows/ontology/:id"
              element={
                <ProtectedRoute>
                  <WorkflowDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflows/process/:id"
              element={
                <ProtectedRoute>
                  <WorkflowDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflows/action-effect/:id"
              element={
                <ProtectedRoute>
                  <ActionEffectMigrationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logs"
              element={
                <ProtectedRoute>
                  <LogsPage />
                </ProtectedRoute>
              }
            />
            {/* Visible to every user: the backend returns all users' session events. */}
            <Route
              path="/session-activity"
              element={
                <ProtectedRoute>
                  <SessionActivityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute adminOnly>
                  <AdminUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/connected-services"
              element={
                <ProtectedRoute adminOnly>
                  <ConnectedServicesPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster />
          <SessionExpiredDialog />
        </div>
      </ThemeProvider>
    </Router>
  );
}

export default App;
